import { ButtonUnstyled, buttonUnstyledClasses } from "@mui/base";
import { MenuItem, styled, IconButton } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React, { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import api from "@/services/api";
import { EmployeeGroup, OrganizationUnit } from "@/models/apiModels";
import popupService from "@/services/popupService";
import { useNavigate } from "react-router-dom";
import { CreateEmployeeRequest } from "@/models/employees";
import { Gender } from "@/models/enums";
import { useTranslation } from "react-i18next";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import authStore from "@/stores/authStore";
import StyledSwitch from "@/components/common/baseSwitch";
import BaseInput from "@/components/common/baseInput";
import BaseDateInput from "@/components/common/baseDateInput";
import ClearIcon from "@/assets/icons/clear-icon.svg";
import { validateEmployeeDates } from "@/util";
import PasswordStrengthMeter from "@/components/common/passwordStrengthMeter/passwordStrengthMeter";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const SaveButton = styled(ButtonUnstyled)(
    () => `
  &:hover {
    background-color: #222C4B;
  }       
  &.${buttonUnstyledClasses.active} {
    background-color: #222C4B;
  } 
  &.${buttonUnstyledClasses.root} {
    padding: 0px 35px;
  } 
  &.${buttonUnstyledClasses.focusVisible} {
    box-shadow: 0 3px 20px 0 rgba(61, 71, 82, 0.1), 0 0 0 5px rgba(0, 127, 255, 0.5);
    outline: none;
  }    
  &.${buttonUnstyledClasses.disabled} {
    opacity: 0.5;
    cursor: not-allowed;
  }`
);

const CreateEmployee = () => {
    const [employee, setEmployee] = useState({
        firstName: "",
        lastName: "",
        workPhone: "",
        mobilePhone: "",
        email: "",
        employeeNumber: "",
        employeeIdCard: "",
        department: "",
        employeeGroup: "",
        empGender: null as number | null,
        employeeStatus: true,
        workStartDate: null as Dayjs | null,
        workEndDate: null as Dayjs | null,
        username: "",
        password: null as string | null,
    });

    const [organizationUnits, setOrganizationUnits] = useState<OrganizationUnit[]>([]);
    const [employeeGroups, setEmployeeGroups] = useState<EmployeeGroup[]>([]);

    const employeesPageURL = "/employees";
    const navigate = useNavigate();
    const { t } = useTranslation();
    const employeeGender = [
        {
            value: Gender.Male,
            label: t("gender.Male"),
        },
        {
            value: Gender.Female,
            label: t("gender.Female"),
        },
    ];

    async function getEmployeesInitData() {
        const employeesListDataResponsePromise = api.getEmployeesPageData();
        const employeesListDataResponse = await employeesListDataResponsePromise;
        setOrganizationUnits(employeesListDataResponse.data.organizationUnits);
        setEmployeeGroups(employeesListDataResponse.data.employeeGroups);
    }

    const handleMobilePhoneChange = (event: any) => {
        const inputValue = event.target.value;
        const isValid = /^[0-9+]+$/.test(inputValue);

        if (inputValue === "") {
            setEmployee((prevData) => ({
                ...prevData,
                mobilePhone: inputValue.substring(0, 15),
            }));
        }
        if (isValid) {
            setEmployee((prevData) => ({
                ...prevData,
                mobilePhone: inputValue.substring(0, 15),
            }));
        }
    };

    const handleWorkPhoneChange = (event: any) => {
        const inputValue = event.target.value;
        const isValid = /^[0-9+]+$/.test(inputValue);

        if (inputValue === "") {
            setEmployee((prevData) => ({
                ...prevData,
                workPhone: inputValue.substring(0, 15),
            }));
        }
        if (isValid) {
            setEmployee((prevData) => ({
                ...prevData,
                workPhone: inputValue.substring(0, 15),
            }));
        }
    };

    const handleChangeStartDate = (newValue: Dayjs | null) => {
        setEmployee((prevData) => ({
            ...prevData,
            workStartDate: newValue,
        }));
    };

    const handleChangeEndDate = (newValue: Dayjs | null) => {
        setEmployee((prevData) => ({
            ...prevData,
            workEndDate: newValue,
        }));
    };

    const handleChangeInput = (field: keyof typeof employee, value: any) => {
        setEmployee((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleGenerateId = (): string => {
        const array = new Uint32Array(4);
        crypto.getRandomValues(array);
        return array.join("-");
    };

    const submitOnClick = async (event: any) => {
        event.preventDefault();
        popupService.showLoading();

        const validDates = await validateEmployeeDates(
            employee.workStartDate?.format("YYYY-MM-DD") ?? null,
            employee.workEndDate?.format("YYYY-MM-DD") ?? null
        );

        if (!validDates) {
            return;
        }

        try {
            const request: CreateEmployeeRequest = {
                organizationUnitId: employee.department,
                employeeGroupId: employee.employeeGroup.toString(),
                employeeNumber: employee.employeeNumber,
                employeePID: employee.employeeIdCard,
                firstName: employee.firstName,
                lastName: employee.lastName,
                workPhone: employee.workPhone,
                mobilePhone: employee.mobilePhone,
                email: employee.email,
                isActive: employee.employeeStatus,
                gender: Number(employee.empGender),
                workStartDate: employee.workStartDate?.format("YYYY-MM-DD") ?? null,
                workEndDate: employee.workEndDate?.format("YYYY-MM-DD") ?? null,
                username: employee.username,
                password: employee.password,
            };
            await api.createEmployee(request);
            await popupService.showSuccess(t("employee.AddedMsg"));
            navigate(employeesPageURL);
        } catch (err) {
            popupService.showApiError(err);
            return;
        }
    };

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            getEmployeesInitData();
        }
        return () => {
            ignore = true;
        };
    }, []);

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            <div className="create-employee-container">
                <form onSubmit={submitOnClick}>
                    <div className="create-employee-header">{t("employee.NewEmployee")}</div>
                    <div className="create-employee-content">
                        <div>
                            <div className="create-employee-data-header font-style-18">
                                <i className="icon-f icon-PersonalInfoIcon fs-26" />
                                {t("employee.PersonalInfo")}
                            </div>
                            <div className="create-employee-data-tab">
                                <div>
                                    <BaseInput
                                        fullWidth
                                        label={t("employee.FirstName")}
                                        size="small"
                                        value={employee.firstName}
                                        autoComplete="new-password"
                                        onChange={(event) => handleChangeInput("firstName", event.target.value)}
                                        inputProps={{
                                            inputMode: "text",
                                            maxLength: 50,
                                        }}
                                        required
                                    />
                                </div>
                                <div>
                                    <BaseInput
                                        fullWidth
                                        label={t("employee.LastName")}
                                        size="small"
                                        value={employee.lastName}
                                        autoComplete="new-password"
                                        onChange={(event) => handleChangeInput("lastName", event.target.value)}
                                        inputProps={{
                                            inputMode: "text",
                                            maxLength: 50,
                                        }}
                                        required
                                    />
                                </div>
                                <div>
                                    <BaseInput
                                        fullWidth
                                        label={t("employee.EmployeeNumber")}
                                        type="number"
                                        size="small"
                                        value={employee.employeeNumber}
                                        autoComplete="new-password"
                                        onChange={(event) => handleChangeInput("employeeNumber", event.target.value)}
                                        inputProps={{
                                            inputMode: "numeric",
                                            maxLength: 40,
                                        }}
                                        required
                                    />
                                </div>
                                <div>
                                    <BaseInput
                                        id={handleGenerateId()}
                                        fullWidth
                                        label={t("user.Username")}
                                        size="small"
                                        value={employee.username}
                                        autoComplete="new-password"
                                        onChange={(event) => handleChangeInput("username", event.target.value)}
                                        inputProps={{
                                            inputMode: "text",
                                            maxLength: 100,
                                        }}
                                        required
                                    />
                                </div>
                                <div>
                                    <BaseInput
                                        select
                                        label={t("employee.Gender")}
                                        size="small"
                                        value={employee.empGender ?? ""}
                                        autoComplete="new-password"
                                        sx={{
                                            width: "100%",
                                        }}
                                        onChange={(event) => handleChangeInput("empGender", Number(event.target.value))}
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton
                                                    className="select-clear-icon"
                                                    sx={{
                                                        visibility: employee.empGender ? "visible" : "hidden",
                                                    }}
                                                    onClick={() => handleChangeInput("empGender", null)}
                                                >
                                                    <img src={ClearIcon} alt="Clear icon" />
                                                </IconButton>
                                            ),
                                        }}
                                    >
                                        {employeeGender.map((gender) => (
                                            <MenuItem key={gender.value} value={gender.value}>
                                                {gender.label}
                                            </MenuItem>
                                        ))}
                                    </BaseInput>
                                </div>
                                <div>
                                    <BaseInput
                                        fullWidth
                                        label={t("employee.IdCard")}
                                        type="number"
                                        size="small"
                                        value={employee.employeeIdCard}
                                        autoComplete="new-password"
                                        onChange={(event) => handleChangeInput("employeeIdCard", event.target.value)}
                                        inputProps={{
                                            inputMode: "numeric",
                                            maxLength: 9,
                                        }}
                                        required
                                    />
                                </div>
                                <div>
                                    <BaseInput
                                        id={handleGenerateId()}
                                        fullWidth
                                        label={t("user.Password")}
                                        size="small"
                                        value={employee.password}
                                        autoComplete="new-password"
                                        onChange={(event) => handleChangeInput("password", event.target.value)}
                                        inputProps={{
                                            inputMode: "text",
                                            maxLength: 50,
                                        }}
                                    />
                                    <PasswordStrengthMeter password={employee.password ?? ""} />
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="create-employee-data-header font-style-18">
                                <i className="icon-f icon-ContactInfoIcon fs-26" />
                                {t("employee.ContactInfo")}
                            </div>
                            <div className="create-employee-data-tab">
                                <div>
                                    <BaseInput
                                        fullWidth
                                        label={t("employee.MobilePhone")}
                                        size="small"
                                        value={employee.mobilePhone}
                                        autoComplete="new-password"
                                        onChange={(event) => handleMobilePhoneChange(event)}
                                        inputProps={{
                                            inputMode: "tel",
                                            maxLength: 15,
                                        }}
                                    />
                                </div>
                                <div>
                                    <BaseInput
                                        fullWidth
                                        label={t("employee.WorkPhone")}
                                        size="small"
                                        value={employee.workPhone}
                                        autoComplete="new-password"
                                        onChange={(event) => handleWorkPhoneChange(event)}
                                        inputProps={{
                                            inputMode: "tel",
                                            maxLength: 15,
                                        }}
                                    />
                                </div>
                                <div>
                                    <BaseInput
                                        fullWidth
                                        label={t("employee.Email")}
                                        size="small"
                                        value={employee.email}
                                        autoComplete="new-password"
                                        onChange={(event) => handleChangeInput("email", event.target.value)}
                                        inputProps={{
                                            inputMode: "email",
                                            maxLength: 255,
                                        }}
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="create-employee-data-header font-style-18">
                                <i className="icon-f icon-WorkPlaceIcon fs-26" />
                                {t("employee.WorkPlace")}
                            </div>
                            <div className="create-employee-data-tab">
                                <div>
                                    <BaseInput
                                        select
                                        label={t("employee.Department")}
                                        value={employee.department}
                                        onChange={(event) =>
                                            handleChangeInput("department", event.target.value.toString())
                                        }
                                        size="small"
                                        sx={{
                                            width: "100%",
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton
                                                    className="select-clear-icon"
                                                    sx={{
                                                        visibility: employee.department ? "visible" : "hidden",
                                                    }}
                                                    onClick={() => handleChangeInput("department", "")}
                                                >
                                                    <img src={ClearIcon} alt="Clear icon" />
                                                </IconButton>
                                            ),
                                        }}
                                    >
                                        {organizationUnits?.map((organizationUnit) => (
                                            <MenuItem key={organizationUnit.id} value={organizationUnit.id}>
                                                {organizationUnit.text}
                                            </MenuItem>
                                        ))}
                                    </BaseInput>
                                </div>
                                <div>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DesktopDatePicker
                                            className="date-picker-input"
                                            label={t("employee.WorkStartDate")}
                                            inputFormat="DD/MM/YYYY"
                                            value={employee.workStartDate}
                                            onChange={handleChangeStartDate}
                                            renderInput={(params) => (
                                                <BaseDateInput autoComplete="new-password" {...params} />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </div>
                                <div>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DesktopDatePicker
                                            className="date-picker-input"
                                            label={t("employee.WorkEndDate")}
                                            inputFormat="DD/MM/YYYY"
                                            value={employee.workEndDate}
                                            onChange={handleChangeEndDate}
                                            renderInput={(params) => (
                                                <BaseDateInput autoComplete="new-password" {...params} />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </div>
                                <div>
                                    <BaseInput
                                        select
                                        label={t("employee.EmployeeGroup")}
                                        size="small"
                                        sx={{
                                            width: "100%",
                                        }}
                                        value={employee.employeeGroup}
                                        autoComplete="new-password"
                                        onChange={(event) =>
                                            handleChangeInput("employeeGroup", event.target.value.toString())
                                        }
                                        required
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton
                                                    className="select-clear-icon"
                                                    sx={{
                                                        visibility: employee.employeeGroup ? "visible" : "hidden",
                                                    }}
                                                    onClick={() => handleChangeInput("employeeGroup", "")}
                                                >
                                                    <img src={ClearIcon} alt="Clear icon" />
                                                </IconButton>
                                            ),
                                        }}
                                    >
                                        {employeeGroups.map((employeeGroup) => (
                                            <MenuItem key={employeeGroup.id} value={employeeGroup.id}>
                                                {employeeGroup.text}
                                            </MenuItem>
                                        ))}
                                    </BaseInput>
                                </div>
                                <div className="create-employee-status font-style-16">
                                    <span>{t("employee.NewEmpStatus")}</span>
                                    <StyledSwitch
                                        value={employee.employeeStatus}
                                        checked={employee.employeeStatus}
                                        onChange={() => handleChangeInput("employeeStatus", !employee.employeeStatus)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="create-employee-buttons">
                        <SaveButton className="basic-button" onClick={() => navigate(employeesPageURL)}>
                            {t("button.cancel")}
                        </SaveButton>
                        <SaveButton type="submit" className="basic-button">
                            {t("button.save")}
                        </SaveButton>
                    </div>
                </form>
            </div>
        </CacheProvider>
    );
};

export default CreateEmployee;
